/**
 * actionCreators.ts
 * ----------------
 * Action creators used throughout both the admin and public portals.
 */

import { AxiosError } from 'axios';
import { Dispatch } from 'redux';

import { isMessageReponse } from '../models/dto/common';
import { Action, AsyncAction, PayloadAction } from './types';

/**
 * Creates a simple action creator.
 * @param type Action name.
 * @returns Action creator.
 */
export function actionCreator (type: string): () => Action {
    return () => ({ type });
}

/**
 * Creates an action creator for failed async actions.
 * @param type Action name.
 * @returns Async action creator.
 */
export function failedAsyncActionCreator (type: string): (...errors: string[]) => AsyncAction {
    return (...errors) => errors.length > 1
        ? { errors, status: 'failed', type }
        : { error: errors[0], status: 'failed', type };
}

/**
 * Creates an action creator for in-progress async actions.
 * @param type Action name.
 * @returns Async action creator.
 */
export function inProgressAsyncActionCreator (type: string): () => AsyncAction {
    return () => ({ status: 'inProgress', type });
}

/**
 * Creates error handlers for axios promises.
 * @param dispatch Function for dispatching Redux actions
 * @param failedActionCreator Action creator for failed responses
 * @returns Error handler for axios promises
 */
export function onAxiosErrorReceived<T> (dispatch: Dispatch, failedActionCreator: (...errors: string[]) => AsyncAction) {
    return (error: AxiosError<T>) => {
        const { response } = error;

        if (response === undefined) {
            console.error(error);
            dispatch(failedActionCreator('An unknown error occured. See logs for more details'));
            return false;
        }

        const { data } = response;

        if (!isMessageReponse(data)) {
            console.error(error);
            dispatch(failedActionCreator('An unknown error occured. See logs for more details'));
            return false;
        }

        console.error(`Error from API: ${data.message}`);
        dispatch(failedActionCreator(data.message));

        return false;
    };
}

/**
 * Creates an action creator for partially succeeded async actions.
 * @param type Action name.
 * @returns Async action creator.
 */
export function partiallySuccessfulAsyncActionCreator (type: string): (...errors: string[]) => AsyncAction {
    return (...errors) => ({ errors, status: 'partiallySucceeded', type });
}

/**
 * Creates an action creator for successful async actions.
 * @param type Action name.
 * @returns Async action creator.
 */
export function successfulAsyncActionCreator (type: string): () => AsyncAction {
    return () => ({ status: 'succeeded', type });
}

/**
 * Creates a payload action creator.
 * @param type Action name.
 * @returns Payload action creator accepting payload as an argument.
 */
export function payloadActionCreator<T> (type: string): (payload: T) => PayloadAction<T> {
    return payload => ({ payload, type });
}